import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  CalendarClock, Plus, Pencil, Trash2, Send, Loader2, Mail, Clock,
  FileText, FileSpreadsheet, CheckCircle2, XCircle, X, History,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const FORMAT_LABELS = { pdf: 'PDF', csv: 'CSV / Excel', excel: 'Excel' };

const blank = {
  name: '', report_type: 'clock', format: 'pdf', frequency: 'weekly',
  day_of_week: 0, day_of_month: 1, month: 1, lookahead_days: 60, hour: 8, minute: 0, branch_id: null,
  directory_view: 'salary', leave_period: 'month', leave_layout: 'summary', leave_salary_basis: 'basic', include_inactive: false,
  recipients: [], subject: '', message: '', active: true, pack_items: [],
};

const fmtNext = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai',
    }) + ' UAE';
  } catch { return iso; }
};

const ScheduledReportsTab = ({ isViewer }) => {
  const [schedules, setSchedules] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [addressBook, setAddressBook] = useState([]);
  const [recipientGroups, setRecipientGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [emailInput, setEmailInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (s) => {
    setHistoryFor(s); setHistoryData([]); setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/api/scheduled-reports/${s.id}/history`, { headers: headers() });
      setHistoryData(r.data?.history || []);
    } catch { toast({ title: 'Failed to load history', variant: 'destructive' }); }
    finally { setHistoryLoading(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, rt, b, ab, rg] = await Promise.all([
        axios.get(`${API}/api/scheduled-reports`, { headers: headers() }),
        axios.get(`${API}/api/scheduled-reports/report-types`, { headers: headers() }),
        axios.get(`${API}/api/clock/branches`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/report-email/recipients`, { headers: headers() }).catch(() => ({ data: { recipients: [] } })),
        axios.get(`${API}/api/scheduled-reports/recipient-groups`, { headers: headers() }).catch(() => ({ data: { groups: [] } })),
      ]);
      setSchedules(s.data || []);
      setReportTypes(rt.data?.report_types || []);
      setBranches(b.data || []);
      setAddressBook(ab.data?.recipients || []);
      setRecipientGroups(rg.data?.groups || []);
    } catch { toast({ title: 'Failed to load scheduled reports', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(blank); setEmailInput(''); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || '', report_type: s.report_type, format: s.format, frequency: s.frequency,
      day_of_week: s.day_of_week ?? 0, day_of_month: s.day_of_month ?? 1,
      month: s.month ?? 1, lookahead_days: s.lookahead_days ?? 60,
      hour: s.hour ?? 8, minute: s.minute ?? 0, branch_id: s.branch_id || null,
      directory_view: s.directory_view || 'salary', leave_period: s.leave_period || 'month', leave_layout: s.leave_layout || 'summary', leave_salary_basis: s.leave_salary_basis || 'basic', include_inactive: !!s.include_inactive,
      recipients: s.recipients || [], subject: s.subject || '', message: s.message || '', active: s.active, pack_items: s.pack_items || [],
    });
    setEmailInput(''); setOpen(true);
  };

  const selectedType = reportTypes.find((rt) => rt.value === form.report_type) || {};
  const allowedFormats = selectedType.formats || ['pdf'];

  // When the report type changes, keep the form coherent: snap format to an
  // allowed one and force monthly frequency for monthly-only reports.
  const onReportTypeChange = (v) => {
    const rt = reportTypes.find((x) => x.value === v) || {};
    const formats = rt.formats || ['pdf'];
    setForm((p) => ({
      ...p,
      report_type: v,
      format: formats.includes(p.format) ? p.format : formats[0],
      frequency: rt.monthly_only ? 'monthly' : 'weekly',
    }));
  };

  const addEmail = (val) => {
    const e = (val || emailInput).trim();
    if (!e) return;
    if (!EMAIL_RE.test(e)) { toast({ title: 'Invalid email', description: e, variant: 'destructive' }); return; }
    if (form.recipients.some((r) => r.toLowerCase() === e.toLowerCase())) { setEmailInput(''); return; }
    setForm((p) => ({ ...p, recipients: [...p.recipients, e] }));
    setEmailInput('');
  };
  const removeEmail = (e) => setForm((p) => ({ ...p, recipients: p.recipients.filter((x) => x !== e) }));

  const applyGroup = (g) => {
    setForm((p) => {
      const set = new Set(p.recipients.map((r) => r.toLowerCase()));
      const merged = [...p.recipients, ...g.emails.filter((e) => !set.has(e.toLowerCase()))];
      return { ...p, recipients: merged };
    });
  };
  const persistGroups = async (groups) => {
    const res = await axios.put(`${API}/api/scheduled-reports/recipient-groups`, { groups }, { headers: headers() });
    setRecipientGroups(res.data?.groups || groups);
  };
  const saveCurrentAsGroup = async () => {
    if (form.recipients.length === 0) { toast({ title: 'Add recipients first', variant: 'destructive' }); return; }
    const name = window.prompt('Group name (e.g. Finance, PRO, Management)');
    if (!name || !name.trim()) return;
    const others = recipientGroups.filter((g) => g.name.toLowerCase() !== name.trim().toLowerCase());
    try {
      await persistGroups([...others, { name: name.trim(), emails: form.recipients }]);
      toast({ title: 'Group saved', description: `"${name.trim()}" with ${form.recipients.length} recipient(s)` });
    } catch { toast({ title: 'Could not save group', variant: 'destructive' }); }
  };
  const deleteGroup = async (name) => {
    try { await persistGroups(recipientGroups.filter((g) => g.name !== name)); }
    catch { toast({ title: 'Could not delete group', variant: 'destructive' }); }
  };

  const save = async () => {
    if (form.recipients.length === 0) { toast({ title: 'Add at least one recipient', variant: 'destructive' }); return; }
    setSaving(true);
    const payload = {
      ...form,
      hour: Number(form.hour), minute: Number(form.minute),
      day_of_week: Number(form.day_of_week), day_of_month: Number(form.day_of_month),
      month: Number(form.month || 1), lookahead_days: Number(form.lookahead_days || 60),
      branch_id: selectedType.supports_branch ? (form.branch_id || null) : null,
    };
    try {
      if (editing) await axios.put(`${API}/api/scheduled-reports/${editing.id}`, payload, { headers: headers() });
      else await axios.post(`${API}/api/scheduled-reports`, payload, { headers: headers() });
      toast({ title: editing ? 'Schedule updated' : 'Schedule created' });
      setOpen(false);
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const toggleActive = async (s, active) => {
    setSchedules((prev) => prev.map((x) => x.id === s.id ? { ...x, active } : x));
    try {
      await axios.put(`${API}/api/scheduled-reports/${s.id}`, {
        name: s.name, report_type: s.report_type, format: s.format, frequency: s.frequency,
        day_of_week: s.day_of_week, day_of_month: s.day_of_month, month: s.month || 1,
        lookahead_days: s.lookahead_days || 60, pack_items: s.pack_items || [],
        hour: s.hour, minute: s.minute,
        branch_id: s.branch_id || null, directory_view: s.directory_view || 'salary',
        leave_period: s.leave_period || 'month', leave_layout: s.leave_layout || 'summary',
        leave_salary_basis: s.leave_salary_basis || 'basic',
        include_inactive: !!s.include_inactive,
        recipients: s.recipients, subject: s.subject, message: s.message, active,
      }, { headers: headers() });
      await load();
    } catch { toast({ title: 'Update failed', variant: 'destructive' }); load(); }
  };

  const doDelete = async () => {
    const s = delTarget; setDelTarget(null);
    try { await axios.delete(`${API}/api/scheduled-reports/${s.id}`, { headers: headers() }); toast({ title: 'Schedule deleted' }); await load(); }
    catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const runNow = async (s) => {
    setRunningId(s.id);
    try {
      const r = await axios.post(`${API}/api/scheduled-reports/${s.id}/run-now`, {}, { headers: headers() });
      toast({ title: 'Report sent', description: `Emailed to ${(r.data.recipients || []).join(', ')}` });
      await load();
    } catch (e) {
      toast({ title: 'Send failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setRunningId(null); }
  };

  return (
    <div className="space-y-4" data-testid="scheduled-reports-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-indigo-600" /> Scheduled Report Auto-Email
          </h3>
          <p className="text-sm text-gray-500">Automatically generate and email reports on a weekly or monthly schedule — no manual steps. Times are UAE (Asia/Dubai).</p>
        </div>
        {!isViewer && (
          <Button onClick={openAdd} className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-testid="add-schedule-btn">
            <Plus className="h-4 w-4" /> New Schedule
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400 text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
      ) : schedules.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400 text-sm" data-testid="schedules-empty">
          <CalendarClock className="h-10 w-10 mx-auto mb-2 text-gray-200" />
          No scheduled reports yet. Create one to auto-email the Clock In/Out report weekly or monthly.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {schedules.map((s) => (
            <Card key={s.id} data-testid={`schedule-row-${s.id}`} className={s.active ? '' : 'opacity-60'}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="font-semibold text-gray-900">{s.name || s.report_label}</span>
                      <Badge variant="outline" className="gap-1 bg-indigo-50 text-indigo-700 border-indigo-200">
                        {s.format === 'csv' ? <FileSpreadsheet className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                        {s.format.toUpperCase()}
                      </Badge>
                      {!s.active && <Badge variant="outline" className="bg-gray-100 text-gray-500">Paused</Badge>}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> {s.schedule_label}</p>
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><Mail className="h-3 w-3" /> {(s.recipients || []).join(', ')}</p>
                    <div className="flex items-center flex-wrap gap-3 mt-1.5 text-[11px]">
                      <span className="text-gray-400">Next: <b className="text-gray-600">{fmtNext(s.next_run_at)}</b></span>
                      {s.last_status === 'sent' && <span className="text-emerald-600 flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" /> Last sent {fmtNext(s.last_run_at)}</span>}
                      {s.last_status === 'failed' && <span className="text-rose-600 flex items-center gap-0.5" title={s.last_error}><XCircle className="h-3 w-3" /> Last attempt failed</span>}
                    </div>
                  </div>
                  {!isViewer && (
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Switch checked={!!s.active} onCheckedChange={(v) => toggleActive(s, v)} data-testid={`schedule-active-${s.id}`} />
                      <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => openHistory(s)} data-testid={`schedule-history-${s.id}`}>
                        <History className="h-3.5 w-3.5" /> History
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => runNow(s)} disabled={runningId === s.id} data-testid={`schedule-run-${s.id}`}>
                        {runningId === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send now
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(s)} data-testid={`schedule-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600" onClick={() => setDelTarget(s)} data-testid={`schedule-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Scheduled Report' : 'New Scheduled Report'}</DialogTitle>
            <DialogDescription>The system will auto-generate and email this report on the schedule below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name (optional)</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Weekly Clock Report" data-testid="schedule-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Report</Label>
                <Select value={form.report_type} onValueChange={onReportTypeChange}>
                  <SelectTrigger data-testid="schedule-report-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {reportTypes.map((rt) => <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Format</Label>
                <Select value={form.format} onValueChange={(v) => setForm((p) => ({ ...p, format: v }))}>
                  <SelectTrigger data-testid="schedule-format"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allowedFormats.map((f) => <SelectItem key={f} value={f}>{FORMAT_LABELS[f] || f.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Staff Directory: which view + active/all */}
            {selectedType.supports_directory_view && (
              <div className="grid grid-cols-2 gap-3" data-testid="schedule-directory-options">
                <div>
                  <Label className="text-xs">Directory view</Label>
                  <Select value={form.directory_view} onValueChange={(v) => setForm((p) => ({ ...p, directory_view: v }))}>
                    <SelectTrigger data-testid="schedule-directory-view"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(selectedType.directory_views || []).map((dv) => <SelectItem key={dv.value} value={dv.value}>{dv.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2 pb-1">
                  <Switch checked={form.include_inactive} onCheckedChange={(v) => setForm((p) => ({ ...p, include_inactive: v }))} data-testid="schedule-include-inactive" />
                  <Label className="text-xs">Include former staff</Label>
                </div>
              </div>
            )}

            {selectedType.supports_leave_period && (
              <div data-testid="schedule-leave-period-options">
                <Label className="text-xs">Leave calendar period</Label>
                <Select value={form.leave_period} onValueChange={(v) => setForm((p) => ({ ...p, leave_period: v }))}>
                  <SelectTrigger data-testid="schedule-leave-period"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(selectedType.leave_periods || []).map((lp) => <SelectItem key={lp.value} value={lp.value}>{lp.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-gray-400 mt-1">Window covered each time it sends (Monthly = last month, Half-Yearly = last completed half, Yearly = that year).</p>
              </div>
            )}

            {selectedType.supports_leave_layout && (
              <div data-testid="schedule-leave-layout-options">
                <Label className="text-xs">Layout</Label>
                <Select value={form.leave_layout} onValueChange={(v) => setForm((p) => ({ ...p, leave_layout: v }))}>
                  <SelectTrigger data-testid="schedule-leave-layout"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(selectedType.leave_layouts || []).map((ll) => <SelectItem key={ll.value} value={ll.value}>{ll.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.leave_layout === 'grid' && form.leave_period !== 'month' && (
                  <p className="text-[11px] text-amber-600 mt-1">Wall-planner grid applies to the Monthly period only — Half-Yearly/Yearly will send the summary table.</p>
                )}
              </div>
            )}

            {selectedType.supports_leave_basis && (
              <div data-testid="schedule-leave-basis-options">
                <Label className="text-xs">Leave salary calculation basis</Label>
                <Select value={form.leave_salary_basis} onValueChange={(v) => setForm((p) => ({ ...p, leave_salary_basis: v }))}>
                  <SelectTrigger data-testid="schedule-leave-basis"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(selectedType.leave_bases || []).map((lb) => <SelectItem key={lb.value} value={lb.value}>{lb.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-gray-400 mt-1">Daily rate used for the leave-salary amount. Basic ÷ 30 is the UAE statutory default; the wider bases are for special / management-approved cases.</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm((p) => ({ ...p, frequency: v }))}>
                  <SelectTrigger data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {!selectedType.monthly_only && <SelectItem value="weekly">Weekly (previous 7 days)</SelectItem>}
                    <SelectItem value="monthly">Monthly (previous month)</SelectItem>
                    {!selectedType.monthly_only && <SelectItem value="yearly">Yearly (previous 12 months)</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {form.frequency === 'weekly' ? (
                <div>
                  <Label className="text-xs">Day of week</Label>
                  <Select value={String(form.day_of_week)} onValueChange={(v) => setForm((p) => ({ ...p, day_of_week: Number(v) }))}>
                    <SelectTrigger data-testid="schedule-weekday"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label className="text-xs">Day of month</Label>
                  <Input type="number" min={1} max={31} value={form.day_of_month} onChange={(e) => setForm((p) => ({ ...p, day_of_month: e.target.value }))} data-testid="schedule-dom" />
                </div>
              )}
            </div>
            {form.frequency === 'yearly' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Month</Label>
                  <Select value={String(form.month || 1)} onValueChange={(v) => setForm((p) => ({ ...p, month: Number(v) }))}>
                    <SelectTrigger data-testid="schedule-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((mn, i) => <SelectItem key={i} value={String(i + 1)}>{mn}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {selectedType.supports_lookahead && (
              <div>
                <Label className="text-xs">Look-ahead window (days)</Label>
                <Input type="number" min={1} max={365} value={form.lookahead_days ?? 60} onChange={(e) => setForm((p) => ({ ...p, lookahead_days: e.target.value }))} data-testid="schedule-lookahead" />
                <p className="text-[11px] text-gray-400 mt-1">How far ahead to include (e.g. 60 = next 60 days). Change to widen or narrow the window.</p>
              </div>
            )}
            {selectedType.supports_pack && (
              <div>
                <Label className="text-xs">Reports to include in the pack</Label>
                <div className="grid grid-cols-2 gap-1 mt-1 max-h-44 overflow-y-auto border rounded-md p-2">
                  {reportTypes.filter((rt) => rt.value !== 'combined_pack').map((rt) => (
                    <label key={rt.value} className="flex items-center gap-2 text-xs py-1 cursor-pointer" data-testid={`pack-item-${rt.value}`}>
                      <input
                        type="checkbox"
                        checked={(form.pack_items || []).includes(rt.value)}
                        onChange={(e) => setForm((p) => ({
                          ...p,
                          pack_items: e.target.checked
                            ? [...(p.pack_items || []), rt.value]
                            : (p.pack_items || []).filter((x) => x !== rt.value),
                        }))}
                      />
                      {rt.label}
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">All selected reports are attached to one email ({(form.pack_items || []).length} selected).</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Send time (UAE)</Label>
                <Input type="time" value={`${String(form.hour).padStart(2, '0')}:${String(form.minute).padStart(2, '0')}`}
                  onChange={(e) => { const [h, m] = e.target.value.split(':'); setForm((p) => ({ ...p, hour: Number(h), minute: Number(m) })); }}
                  data-testid="schedule-time" />
              </div>
              {selectedType.supports_branch && (
                <div>
                  <Label className="text-xs">Station</Label>
                  <Select value={form.branch_id || 'all'} onValueChange={(v) => setForm((p) => ({ ...p, branch_id: v === 'all' ? null : v }))}>
                    <SelectTrigger data-testid="schedule-branch"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All stations</SelectItem>
                      {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Recipients */}
            <div>
              <Label className="text-xs">Recipients</Label>
              <div className="flex gap-2">
                <Input value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                  placeholder="email@company.com — Enter to add" data-testid="schedule-email-input" />
                <Button type="button" variant="outline" onClick={() => addEmail()} data-testid="schedule-email-add">Add</Button>
              </div>
              {addressBook.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {addressBook.filter((a) => !form.recipients.some((r) => r.toLowerCase() === a.toLowerCase())).map((a) => (
                    <button key={a} type="button" onClick={() => addEmail(a)} className="text-[11px] bg-gray-100 hover:bg-gray-200 rounded-full px-2 py-0.5 text-gray-600">+ {a}</button>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="schedule-recipient-chips">
                {form.recipients.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs rounded-full pl-2.5 pr-1 py-0.5">
                    {e}
                    <button type="button" onClick={() => removeEmail(e)} className="hover:bg-indigo-100 rounded-full p-0.5"><X className="h-3 w-3" /></button>
                  </span>
                ))}
                {form.recipients.length === 0 && <span className="text-[11px] text-gray-400">No recipients yet</span>}
              </div>

              {/* Saved recipient groups (Finance / PRO / Management) */}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-gray-500">Groups:</span>
                {recipientGroups.length === 0 && <span className="text-[11px] text-gray-400">none saved</span>}
                {recipientGroups.map((g) => (
                  <span key={g.name} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[11px] rounded-full pl-2 pr-1 py-0.5" data-testid={`recipient-group-${g.name}`}>
                    <button type="button" onClick={() => applyGroup(g)} title={`Add ${g.emails.length} email(s)`}>{g.name} ({g.emails.length})</button>
                    <button type="button" onClick={() => deleteGroup(g.name)} className="hover:bg-emerald-100 rounded-full p-0.5" title="Delete group"><X className="h-3 w-3" /></button>
                  </span>
                ))}
                <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px] text-indigo-600" onClick={saveCurrentAsGroup} data-testid="save-recipient-group-btn">+ Save as group</Button>
              </div>
            </div>

            <div>
              <Label className="text-xs">Email subject (optional)</Label>
              <Input value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} placeholder="Defaults to report name + period" data-testid="schedule-subject" />
            </div>
            <div>
              <Label className="text-xs">Message (optional)</Label>
              <Textarea value={form.message} onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))} rows={2} placeholder="Optional note included in the email body" data-testid="schedule-message" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm((p) => ({ ...p, active: v }))} data-testid="schedule-active-toggle" />
              <Label className="text-xs">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="schedule-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} {editing ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery history dialog */}
      <Dialog open={!!historyFor} onOpenChange={(o) => { if (!o) setHistoryFor(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="schedule-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-indigo-600" /> Delivery History</DialogTitle>
            <DialogDescription>{historyFor?.name || historyFor?.report_label} — last 10 sends</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-8 text-center text-gray-400 text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
          ) : historyData.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm" data-testid="history-empty">No sends yet. Use "Send now" or wait for the next scheduled run.</div>
          ) : (
            <div className="divide-y border rounded-lg" data-testid="history-list">
              {historyData.map((h, i) => (
                <div key={i} className="p-3 flex items-start gap-3" data-testid={`history-row-${i}`}>
                  {h.status === 'sent'
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    : <XCircle className="h-4 w-4 text-rose-600 mt-0.5 flex-shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className={`text-sm font-medium ${h.status === 'sent' ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {h.status === 'sent' ? 'Sent' : 'Failed'}
                      </span>
                      <Badge variant="outline" className="text-[10px]">{h.trigger === 'manual' ? 'Manual' : 'Auto'}</Badge>
                      <span className="text-xs text-gray-500">{fmtNext(h.at)}</span>
                    </div>
                    {(h.period_start || h.period_end) && (
                      <p className="text-[11px] text-gray-500 mt-0.5">Period: {h.period_start} → {h.period_end}</p>
                    )}
                    {h.recipients?.length > 0 && (
                      <p className="text-[11px] text-gray-500 mt-0.5 truncate">To: {h.recipients.join(', ')}</p>
                    )}
                    {h.error && <p className="text-[11px] text-rose-600 mt-0.5">{h.error}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => { if (!o) setDelTarget(null); }}>
        <AlertDialogContent data-testid="schedule-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete schedule?</AlertDialogTitle>
            <AlertDialogDescription>"{delTarget?.name || delTarget?.report_label}" will stop auto-emailing. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ScheduledReportsTab;
